export interface GeminiModelPricing {
    inputCostPerToken: number; // USD per token
    outputCostPerToken: number; // USD per token
}

// Prices from https://ai.google.dev/gemini-api/docs/pricing in $/token
// Note: Using standard tier pricing (prompts <= 200k tokens)
export const GEMINI_PRICING: Record<string, GeminiModelPricing> = {
    // Gemini 3.1 Pro Preview
    "gemini-3-1-pro-preview": {
        inputCostPerToken: 2.0 / 1_000_000,
        outputCostPerToken: 12.0 / 1_000_000
    },

    // Gemini 3 Pro Preview
    "gemini-3-pro-preview": {
        inputCostPerToken: 2.0 / 1_000_000,
        outputCostPerToken: 12.0 / 1_000_000
    },

    // Gemini 3 Flash Preview
    "gemini-3-flash-preview": {
        inputCostPerToken: 0.5 / 1_000_000,
        outputCostPerToken: 3.0 / 1_000_000
    },

    // Gemini 2.5 Pro
    "gemini-2-5-pro": { inputCostPerToken: 1.25 / 1_000_000, outputCostPerToken: 10.0 / 1_000_000 },

    // Gemini 2.5 Flash
    "gemini-2-5-flash": { inputCostPerToken: 0.3 / 1_000_000, outputCostPerToken: 2.5 / 1_000_000 },

    // Gemini 2.5 Flash-Lite
    "gemini-2-5-flash-lite": {
        inputCostPerToken: 0.1 / 1_000_000,
        outputCostPerToken: 0.4 / 1_000_000
    },

    // Gemini 2.0 Flash
    "gemini-2-0-flash": { inputCostPerToken: 0.1 / 1_000_000, outputCostPerToken: 0.4 / 1_000_000 },

    // Gemini 2.0 Flash-Lite
    "gemini-2-0-flash-lite": {
        inputCostPerToken: 0.075 / 1_000_000,
        outputCostPerToken: 0.3 / 1_000_000
    }
};

/**
 * Convert cost per token (USD) to Roboflow credits conversion rate.
 * Same formula as OpenAI: (cost * margin) / credit_price
 */
export function priceToCreditConversion(costPerTokenUSD: number): number {
    const CREDIT_PRICE_USD = 3;
    const MARGIN = 1.5;
    return (costPerTokenUSD * MARGIN) / CREDIT_PRICE_USD;
}

/**
 * Slugify a Gemini model name for use in billing feature names.
 * Converts "gemini-3-pro-preview" -> "gemini-3-pro-preview"
 * Converts "gemini-2.5-pro" -> "gemini-2-5-pro"
 */
export function slugifyGeminiModel(model: string): string {
    return model.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
}

/**
 * Get conversion rate for a specific Gemini model and token type.
 * Returns null if model is not found.
 */
export function getConversionRateForGeminiModel(
    model: string,
    type: "input" | "output"
): number | null {
    const slug = slugifyGeminiModel(model);
    const pricing = GEMINI_PRICING[slug];
    if (!pricing) return null;
    const cost = type === "input" ? pricing.inputCostPerToken : pricing.outputCostPerToken;
    return priceToCreditConversion(cost);
}
